import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Plus, X, Loader2, Image as ImageIcon, Calendar, MapPin, User, ArrowLeft, Pencil } from 'lucide-react';
import { RootState } from '../store/store';
import { selectUser } from '../store/slices/authSlice';
import { newsAPI } from '../services/api';
import { MEDITERRANEAN_REGIONS, getRegionsByCountry, getCityOptionsForAddress } from '../constants/mediterraneanRegions';
import { getImageUrl } from '../utils/imageUtils';

const NEWS_CATEGORY_IDS = ['equipment_materials', 'research_development', 'finance_economic', 'weather', 'others'] as const;
const CATEGORY_LABELS: Record<string, string> = {
  equipment_materials: 'Equipment & Materials',
  research_development: 'Research & Development',
  finance_economic: 'Finance & Economic',
  weather: 'Weather',
  others: 'Others',
};

const EditNewsPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const user = useSelector((state: RootState) => selectUser(state));

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [url, setUrl] = useState('');
  const [category, setCategory] = useState<string>('others');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [existingImageUrl, setExistingImageUrl] = useState<string | null>(null);
  const [imageRemoved, setImageRemoved] = useState(false);
  const [imageCaption, setImageCaption] = useState('');
  const [galleryFiles, setGalleryFiles] = useState<File[]>([]);
  const [galleryPreviews, setGalleryPreviews] = useState<string[]>([]);
  const [existingGalleryUrls, setExistingGalleryUrls] = useState<string[]>([]);
  const [videoUrl, setVideoUrl] = useState('');
  const [publishDate, setPublishDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [publishTime, setPublishTime] = useState(() => new Date().toTimeString().slice(0, 5));
  const [status, setStatus] = useState<'draft' | 'published' | 'scheduled'>('draft');
  const [visibility, setVisibility] = useState<'public' | 'private' | 'members_only'>('public');
  const [authorName, setAuthorName] = useState('');
  const [authorRole, setAuthorRole] = useState<string>('');
  const [sourceLink, setSourceLink] = useState('');
  const [externalSourceName, setExternalSourceName] = useState('');
  const [country, setCountry] = useState('');
  const [region, setRegion] = useState('');
  const [city, setCity] = useState('');
  const [isFeatured, setIsFeatured] = useState(false);
  const [allowComments, setAllowComments] = useState(true);

  useEffect(() => {
    if (!id || !user?._id) return;
    const fetchNews = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await newsAPI.getCommunityNewsView(id);
        if (res.data?.success && res.data?.data) {
          const n = res.data.data;
          const creatorId = String(n.creator?._id || n.creator);
          if (creatorId !== String(user._id) && user.role?.toLowerCase() !== 'admin') {
            setError('You can only edit your own news');
            setLoading(false);
            return;
          }
          setTitle(n.title || '');
          setDescription(n.description || '');
          setUrl(n.url || '');
          setCategory(n.category || 'others');
          setImageCaption(n.imageCaption || '');
          setVideoUrl(n.videoUrl || '');
          setPublishDate(n.publishDate ? new Date(n.publishDate).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10));
          setPublishTime(n.publishTime || new Date().toTimeString().slice(0, 5));
          setStatus((n.status as any) || 'draft');
          setVisibility((n.visibility as any) || 'public');
          setAuthorName(n.authorName || '');
          setAuthorRole(['scientific', 'farmer', 'etudiant', 'rechercher', 'others'].includes(String(n.authorRole || '')) ? (n.authorRole as string) : '');
          setSourceLink(n.sourceLink || '');
          setExternalSourceName(n.externalSourceName || '');
          setCountry(n.country || '');
          setRegion(n.region || '');
          setCity(n.city || '');
          setIsFeatured(n.isFeatured || false);
          setAllowComments(n.allowComments !== false);
          if (n.image) {
            setExistingImageUrl(getImageUrl(n.image));
          }
          if (n.gallery?.length) {
            setExistingGalleryUrls(n.gallery.map((p: string) => getImageUrl(p)).filter(Boolean) as string[]);
          }
        } else {
          setError('News not found');
        }
      } catch (err: any) {
        setError(err.response?.data?.message || err.message || 'Failed to load news');
      } finally {
        setLoading(false);
      }
    };
    fetchNews();
  }, [id, user?._id]);

  if (!user?._id) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-gray-600 mb-4">You must be logged in to edit news.</p>
          <Link to="/login" className="text-indigo-600 hover:text-indigo-700 font-medium">Log in</Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Loader2 className="h-10 w-10 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (error && !title) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <Link to="/news" className="text-indigo-600 hover:text-indigo-700 font-medium">Back to News</Link>
        </div>
      </div>
    );
  }

  const mediterraneanCountries = Object.keys(MEDITERRANEAN_REGIONS).sort();
  const regionOptions = country ? getRegionsByCountry(country) : [];
  const cityOptions = country && region ? getCityOptionsForAddress(country, region) : [];

  const handleCountryChange = (value: string) => {
    setCountry(value);
    setRegion('');
    setCity('');
  };
  const handleRegionChange = (value: string) => {
    setRegion(value);
    setCity('');
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Invalid file type. Use JPEG, PNG, GIF, or WebP.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Image is too large. Maximum size is 5MB.');
      return;
    }
    setError(null);
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    setImageRemoved(false);
  };

  const removeImage = () => {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImageFile(null);
    setImagePreview(null);
    setImageRemoved(true);
  };

  const handleGalleryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    const valid = files.filter((f) => allowed.includes(f.type) && f.size <= 5 * 1024 * 1024);
    setGalleryFiles((prev) => [...prev, ...valid].slice(0, 10));
    setGalleryPreviews((prev) => [...prev, ...valid.map((f) => URL.createObjectURL(f))].slice(0, 10));
  };

  const removeGalleryImage = (idx: number) => {
    if (idx < galleryPreviews.length) URL.revokeObjectURL(galleryPreviews[idx]);
    setGalleryFiles((prev) => prev.filter((_, i) => i !== idx));
    setGalleryPreviews((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !title.trim() || !description.trim()) return;

    setSaving(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('title', title.trim());
      fd.append('description', description.trim());
      fd.append('category', category || 'others');
      if (url.trim()) fd.append('url', url.trim());
      if (imageFile) fd.append('image', imageFile);
      if (imageRemoved) fd.append('removeImage', 'true');
      if (imageCaption.trim()) fd.append('imageCaption', imageCaption.trim());
      galleryFiles.forEach((f) => fd.append('gallery', f));
      if (videoUrl.trim()) fd.append('videoUrl', videoUrl.trim());
      fd.append('publishDate', publishDate);
      fd.append('publishTime', publishTime);
      fd.append('status', status);
      fd.append('visibility', visibility);
      if (authorName.trim()) fd.append('authorName', authorName.trim());
      if (authorRole) fd.append('authorRole', authorRole);
      if (sourceLink.trim()) fd.append('sourceLink', sourceLink.trim());
      if (externalSourceName.trim()) fd.append('externalSourceName', externalSourceName.trim());
      if (country.trim()) fd.append('country', country.trim());
      if (region.trim()) fd.append('region', region.trim());
      if (city.trim()) fd.append('city', city.trim());
      fd.append('isFeatured', String(isFeatured));
      fd.append('allowComments', String(allowComments));
      fd.append('isPublic', visibility === 'public' ? 'true' : 'false');

      const res = await newsAPI.updateCommunityNews(id, fd);
      if (res.data?.success) {
        navigate('/news');
      } else {
        setError(res.data?.message || 'Failed to update news');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to update news');
    } finally {
      setSaving(false);
    }
  };

  const showImagePreview = imagePreview || (existingImageUrl && !imageRemoved);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <Link to="/news" className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors">
            <ArrowLeft className="h-4 w-4" />
            Back to News
          </Link>
        </div>

        <form onSubmit={handleSubmit} className="border border-gray-200 rounded-xl bg-white p-6 shadow-sm">
          <h1 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <Pencil className="h-6 w-6" />
            Edit news
          </h1>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>
          )}

          <div className="space-y-4 mb-6">
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Title"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 bg-white"
            />
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Description"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 bg-white h-24 resize-none"
            />
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Link (optional)</label>
                <input
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 bg-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 bg-white"
                >
                  {NEWS_CATEGORY_IDS.map((id) => (
                    <option key={id} value={id}>{CATEGORY_LABELS[id] || id}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <ImageIcon className="h-4 w-4" />
              Media
            </h4>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Featured Image</label>
                <div className="flex gap-2 items-center">
                  <label className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-600 bg-white cursor-pointer hover:bg-gray-50 truncate">
                    {imageFile ? imageFile.name : 'Choose image'}
                    <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
                  </label>
                  {showImagePreview && (
                    <div className="flex items-center gap-2">
                      <img src={imagePreview || existingImageUrl || ''} alt="" className="w-16 h-16 object-cover rounded border" />
                      <button type="button" onClick={removeImage} className="p-2 text-red-600 hover:bg-red-50 rounded-lg">
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Image Caption</label>
                <input
                  value={imageCaption}
                  onChange={(e) => setImageCaption(e.target.value)}
                  placeholder="Caption for the featured image"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 bg-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Gallery</label>
                <div className="flex flex-wrap gap-2">
                  {existingGalleryUrls.map((url, i) => (
                    <img key={`ex-${i}`} src={url} alt="" className="w-16 h-16 object-cover rounded border" />
                  ))}
                  {galleryPreviews.map((p, i) => (
                    <div key={i} className="relative">
                      <img src={p} alt="" className="w-16 h-16 object-cover rounded border" />
                      <button type="button" onClick={() => removeGalleryImage(i)} className="absolute -top-1 -right-1 p-1 bg-red-500 text-white rounded-full text-xs">
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                  {(existingGalleryUrls.length + galleryFiles.length) < 10 && (
                    <label className="w-16 h-16 border-2 border-dashed border-gray-300 rounded flex items-center justify-center cursor-pointer hover:bg-gray-100 text-gray-400">
                      <Plus className="h-6 w-6" />
                      <input type="file" accept="image/*" multiple onChange={handleGalleryChange} className="hidden" />
                    </label>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Video URL</label>
                <input
                  value={videoUrl}
                  onChange={(e) => setVideoUrl(e.target.value)}
                  placeholder="https://www.youtube.com/watch?v=..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 bg-white"
                />
              </div>
            </div>
          </div>

          <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Publishing
            </h4>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Publish Date</label>
                <input
                  type="date"
                  value={publishDate}
                  onChange={(e) => setPublishDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 bg-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Publish Time</label>
                <input
                  type="time"
                  value={publishTime}
                  onChange={(e) => setPublishTime(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 bg-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as any)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 bg-white"
                >
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                  <option value="scheduled">Scheduled</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Visibility</label>
                <select
                  value={visibility}
                  onChange={(e) => setVisibility(e.target.value as any)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 bg-white"
                >
                  <option value="public">Public</option>
                  <option value="private">Private</option>
                  <option value="members_only">Members only</option>
                </select>
              </div>
            </div>
          </div>

          <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <User className="h-4 w-4" />
              Author & Source
            </h4>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Author Name</label>
                <input
                  value={authorName}
                  onChange={(e) => setAuthorName(e.target.value)}
                  placeholder="Author name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 bg-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Author Role</label>
                <select
                  value={authorRole}
                  onChange={(e) => setAuthorRole(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 bg-white"
                >
                  <option value="">—</option>
                  <option value="scientific">Scientific</option>
                  <option value="farmer">Farmer</option>
                  <option value="etudiant">Student</option>
                  <option value="rechercher">Researcher</option>
                  <option value="others">Others</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Source Link</label>
                <input
                  value={sourceLink}
                  onChange={(e) => setSourceLink(e.target.value)}
                  placeholder="https://..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 bg-white"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">External Source Name</label>
                <input
                  value={externalSourceName}
                  onChange={(e) => setExternalSourceName(e.target.value)}
                  placeholder="Original source name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 bg-white"
                />
              </div>
            </div>
          </div>

          <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              Location
            </h4>
            <div className="grid gap-4 sm:grid-cols-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                <select
                  value={country}
                  onChange={(e) => handleCountryChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 bg-white"
                >
                  <option value="">—</option>
                  {mediterraneanCountries.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Region</label>
                <select
                  value={region}
                  onChange={(e) => handleRegionChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 bg-white"
                >
                  <option value="">—</option>
                  {regionOptions.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                <select
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 bg-white"
                >
                  <option value="">—</option>
                  {cityOptions.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="mb-6 flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={isFeatured} onChange={(e) => setIsFeatured(e.target.checked)} className="rounded" />
              <span className="text-sm text-gray-700">Featured</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={allowComments} onChange={(e) => setAllowComments(e.target.checked)} className="rounded" />
              <span className="text-sm text-gray-700">Allow comments</span>
            </label>
          </div>

          <div className="flex gap-3">
            <button
              type="submit"
              disabled={saving || !title.trim() || !description.trim()}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Save changes
            </button>
            <Link to="/news" className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
              Cancel
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditNewsPage;
